import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import {
  Avatar,
  AvatarSize,
  Button,
  Divider,
  DividerVariants,
  Dropdown,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { CountryCode } from "../../../../utils/CountryCode";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { updateUserMyAccountSagaAction } from "../../../../redux/saga/user/saga-actions";
import "./UserDetail.scss";

const CountryCodeOptions = CountryCode.map((country) => {
  return {
    label: country.name,
    value: country.dial_code,
  };
});

const CountryOptions = CountryCode.map((country) => {
  return { label: country.name, value: country.name };
});

const UserDetail = () => {
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [userDetails, setUserDetails] = React.useState({
    firstName: "",
    lastName: "",
    dialCode: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
  });
  const [phoneStatus, setPhoneStatus] = React.useState<
    { status: "success" | "error" } | {}
  >({});
  const [errorMsg, setErrorMsg] = React.useState({
    emailMsg: "",
    phoneMsg: "",
  });
  const [selected, setSelected] = React.useState(null);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const { status } = useAppSelector((state) => state.user);
  const { loggedUserDetail, status: authStatus } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const digitRegex = /^\d+$/;

  React.useEffect(() => {
    const parsedPhoneNumber = parsePhoneNumberFromString(
      loggedUserDetail.phone_number ?? ""
    );
    let dialCode = "";
    let nationalNo = "";
    if (parsedPhoneNumber) {
      dialCode = "+" + parsedPhoneNumber.countryCallingCode;
      nationalNo = parsedPhoneNumber.nationalNumber;
      const country = CountryCode.find(
        (country) => country.code === parsedPhoneNumber.country
      );
      if (country)
        setSelected({
          label: country.name,
          value: country.dial_code,
        });
    }
    const country = CountryOptions.find(
      (country) => country.value === loggedUserDetail.country
    );
    if (country) setSelectedCountry(country);
    setUserDetails((prev) => {
      return {
        ...prev,
        firstName: loggedUserDetail.first_name,
        lastName: loggedUserDetail.last_name,
        dialCode: dialCode,
        phoneNumber: nationalNo,
        country: loggedUserDetail.country,
        state: loggedUserDetail.province,
        city: loggedUserDetail.city,
        zip: loggedUserDetail.zip,
        address: loggedUserDetail.address1,
      };
    });
  }, [loggedUserDetail]);

  React.useEffect(() => {
    if (userDetails.phoneNumber.length === 0) {
      setPhoneStatus({});
      setErrorMsg({ ...errorMsg, phoneMsg: "" });
    } else {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        userDetails.dialCode + userDetails.phoneNumber
      );
      if (
        parsedPhoneNumber &&
        parsedPhoneNumber.isValid() &&
        digitRegex.test(userDetails.phoneNumber)
      ) {
        setPhoneStatus({});
        setErrorMsg({ ...errorMsg, phoneMsg: "" });
      }
    }
  }, [userDetails.phoneNumber, userDetails.dialCode]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
    setUserDetails({
      ...userDetails,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const validatePhone = () => {
    const parsedPhoneNumber = parsePhoneNumberFromString(
      userDetails.dialCode + userDetails.phoneNumber
    );
    if (userDetails.phoneNumber.length > 0) {
      if (
        !parsedPhoneNumber ||
        !parsedPhoneNumber.isValid() ||
        !digitRegex.test(userDetails.phoneNumber)
      ) {
        setPhoneStatus({ status: "error" });
        setErrorMsg({ ...errorMsg, phoneMsg: "Enter a valid phone number" });
      }
    }
  };

  const onCountryCodeSelect = (data: { label: string; value: string }) => {
    let value = data ? data.value : "";
    setSelected(data);
    setUserDetails({
      ...userDetails,
      dialCode: value,
    });
    if (data) {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        data.value + userDetails.phoneNumber
      );
      if (userDetails.phoneNumber.length > 0) {
        if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
          setPhoneStatus({ status: "error" });
          setErrorMsg({ ...errorMsg, phoneMsg: "Enter a valid phone number" });
        }
      }
    }
  };

  const onCountrySelect = (data: { label: string; value: string }) => {
    let value = data ? data.value : "";
    setSelectedCountry(data);
    setUserDetails({
      ...userDetails,
      country: value,
    });
  };

  const onSaveButtonClick = () => {
    const submitData = {
      ID: loggedUserDetail.ID,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email: loggedUserDetail.email,
      phone: userDetails.dialCode + userDetails.phoneNumber,
      status: loggedUserDetail.status,
      role: loggedUserDetail.role,
      address1: userDetails.address,
      city: userDetails.city,
      province: userDetails.state,
      country: userDetails.country,
      zip: userDetails.zip,
      partner: loggedUserDetail.metadata.partner,
    };
    dispatch(updateUserMyAccountSagaAction(submitData));
    setEditMode(false);
  };

  const onCancelButtonClick = () => {
    const parsedPhoneNumber = parsePhoneNumberFromString(
      loggedUserDetail.phone_number ?? ""
    );
    let dialCode = "";
    let nationalNo = "";
    if (parsedPhoneNumber) {
      dialCode = "+" + parsedPhoneNumber.countryCallingCode;
      nationalNo = parsedPhoneNumber.nationalNumber;
      const country = CountryCode.find(
        (country) => country.code === parsedPhoneNumber.country
      );
      if (country)
        setSelected({
          label: country.name,
          value: country.dial_code,
        });
    }
    const country = CountryOptions.find(
      (country) => country.value === loggedUserDetail.country
    );
    if (country) setSelectedCountry(country);
    setUserDetails((prev) => {
      return {
        ...prev,
        firstName: loggedUserDetail.first_name,
        lastName: loggedUserDetail.last_name,
        dialCode: dialCode,
        phoneNumber: nationalNo,
        country: loggedUserDetail.country,
        state: loggedUserDetail.province,
        city: loggedUserDetail.city,
        zip: loggedUserDetail.zip,
        address: loggedUserDetail.address1,
      };
    });
    setEditMode(false);
  };

  const isSaveButtonEnabled =
    userDetails.firstName && userDetails.lastName && !errorMsg.phoneMsg;

  const returnDashForEmptyString = (data: string) => {
    if (data) return data;
    return <span>-</span>;
  };

  return (
    <div className="user-detail-container">
      {status.userDetailLoading ||
      status.updateUserLoading ||
      authStatus.loggedUserDetailLoading ? (
        <div
          style={{
            padding: "64px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <>
          <div className="row-1">
            <h1>Name & Avatar</h1>
            {editMode ? (
              <div style={{ display: "flex", gap: "12px" }}>
                <Button
                  label="Cancel"
                  variant="secondary"
                  onClick={onCancelButtonClick}
                />
                <Button
                  label="Save"
                  variant="primary"
                  disabled={!isSaveButtonEnabled}
                  onClick={onSaveButtonClick}
                />
              </div>
            ) : (
              <Button
                label="Edit"
                variant="secondary"
                icon={<Iconography icon="pen" />}
                onClick={() => setEditMode(true)}
              />
            )}
          </div>
          <div className="row-2">
            {false ? (
              <Avatar
                size={AvatarSize.XXLarge}
                isEditable={false}
                image="https://gw-assets.assaabloy.com/is/image/assaabloy/divisions-card-list-16x9:16x9-M"
              />
            ) : (
              <Avatar size={AvatarSize.XXLarge} isEditable={false} />
            )}
            {editMode ? (
              <div className="name-input-wrapper">
                <div className="input-wrapper">
                  <TextField
                    label="First Name"
                    id="firstName"
                    name="firstName"
                    placeholder="Enter your first name"
                    value={userDetails.firstName}
                    theme="dark"
                    onChange={handleChange}
                  />
                </div>
                <div className="input-wrapper">
                  <TextField
                    label="Last Name"
                    id="lastName"
                    name="lastName"
                    placeholder="Enter your last name"
                    value={userDetails.lastName}
                    theme="dark"
                    onChange={handleChange}
                  />
                </div>
              </div>
            ) : (
              <h2>
                <span>{loggedUserDetail.first_name}</span>{" "}
                <span>{loggedUserDetail.last_name}</span>
              </h2>
            )}
          </div>
          <Divider mode={DividerVariants.Content} />
          <div className="row-3">
            <h1>Details</h1>
          </div>
          <div className="row-4">
            <div className="info-group">
              <div className="icon-wrapper">
                <Iconography icon="envelop" size={IconographySizes.Medium} />
              </div>
              <div className="info">
                <label>E-mail ID</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="email"
                      name="email"
                      placeholder="Enter your E-mail"
                      value={loggedUserDetail.email}
                      theme="dark"
                      onChange={() => {}}
                      disabled={true}
                    />
                  </div>
                ) : (
                  <p>{loggedUserDetail.email}</p>
                )}
              </div>
            </div>
            <div className="info-group">
              <div className="icon-wrapper">
                <Iconography icon="usersGear" size={IconographySizes.Medium} />
              </div>
              <div className="info">
                <label>Role</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="partnerCompanyName"
                      name="Partner Company Name"
                      placeholder="Partner Company Name"
                      value={loggedUserDetail.role}
                      theme="dark"
                      onChange={() => {}}
                      disabled={true}
                    />
                  </div>
                ) : (
                  <p>{loggedUserDetail.role}</p>
                )}
              </div>
            </div>
            <div className="info-group">
              <div className="icon-wrapper">
                <Iconography icon="phone" size={IconographySizes.Medium} />
              </div>
              <div className="info">
                <label>Phone Number</label>
                {editMode ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div className="dropdown-wrapper">
                      <Dropdown
                        id="countryCode"
                        placeholder="Country"
                        options={CountryCodeOptions}
                        selected={selected}
                        onSelect={onCountryCodeSelect}
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div className="phone-wrapper">
                      <TextField
                        {...phoneStatus}
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder="Phone number"
                        value={userDetails.phoneNumber}
                        theme="dark"
                        helperText={errorMsg.phoneMsg}
                        onChange={handleChange}
                        onBlur={validatePhone}
                      />
                    </div>
                  </div>
                ) : (
                  <p>
                    {returnDashForEmptyString(loggedUserDetail.phone_number)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="row-4">
            <div className="info-group">
              <div
                className="icon-wrapper"
                style={{ marginTop: editMode ? "18px" : "" }}
              >
                <Iconography icon="globe" size={IconographySizes.Medium} />
              </div>
              <div className="info">
                <label>Country</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <Dropdown
                      id="country"
                      placeholder="Country"
                      options={CountryOptions}
                      selected={selectedCountry}
                      onSelect={onCountrySelect}
                      isClearable={true}
                    />
                  </div>
                ) : (
                  <p>{returnDashForEmptyString(loggedUserDetail.country)}</p>
                )}
              </div>
            </div>
            <div className="info-group">
              <div
                className="icon-wrapper"
                style={{ marginTop: editMode ? "18px" : "" }}
              >
                <Iconography
                  icon="locationDot"
                  size={IconographySizes.Medium}
                />
              </div>
              <div className="info">
                <label>State</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="state"
                      name="state"
                      placeholder=""
                      value={userDetails.state}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <p>{returnDashForEmptyString(loggedUserDetail.province)}</p>
                )}
              </div>
            </div>
            <div className="info-group">
              <div
                className="icon-wrapper"
                style={{ marginTop: editMode ? "18px" : "" }}
              >
                <Iconography
                  icon="locationDot"
                  size={IconographySizes.Medium}
                />
              </div>
              <div className="info">
                <label>City</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="city"
                      name="city"
                      value={userDetails.city}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <p>{returnDashForEmptyString(loggedUserDetail.city)}</p>
                )}
              </div>
            </div>
          </div>
          <div className="row-4">
            <div className="info-group">
              <div
                className="icon-wrapper"
                style={{ marginTop: editMode ? "18px" : "" }}
              >
                <Iconography
                  icon="locationDot"
                  size={IconographySizes.Medium}
                />
              </div>
              <div className="info">
                <label>Zip Code</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="zip"
                      name="zip"
                      value={userDetails.zip}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <p>{returnDashForEmptyString(loggedUserDetail.zip)}</p>
                )}
              </div>
            </div>
            <div className="info-group">
              <div
                className="icon-wrapper"
                style={{ marginTop: editMode ? "18px" : "" }}
              >
                <Iconography
                  icon="locationDot"
                  size={IconographySizes.Medium}
                />
              </div>
              <div className="info">
                <label>Street Address</label>
                {editMode ? (
                  <div className="input-wrapper">
                    <TextField
                      id="address"
                      name="address"
                      placeholder=""
                      value={userDetails.address}
                      theme="dark"
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <p>{returnDashForEmptyString(loggedUserDetail.address1)}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDetail;
